import { useEffect, useState } from 'react';
import API from '../api';

// ── 스토리지 백업 모달 ──────────────────────────────────────────────────
// "스토리지 - 파일 업로드 버튼 옆에 스토리지 백업 버튼 추가" 요청. 서버 컴퓨터에 물리적으로
// 장착된 하드디스크/USB 중 하나를 골라 D:\storage 전체를 1:1 미러 복사(robocopy /MIR)한다.
// 미러 복사는 대상 드라이브에만 있던 파일/폴더를 지워버리는 되돌릴 수 없는 동작이라, 실행 전
// 반드시 명시적 확인을 받는다. 이 기능은 서버 로컬(localhost) 접속 + 최고관리자/관리자만
// 사용할 수 있다(백엔드 routes/storageBackup.js가 localOnly+isSuperAdmin으로 게이팅).
const fmtBytes = (n) => {
  if (!n || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};
const fmtDT = (str) => {
  if (!str) return '-';
  const d = new Date(str.replace(' ', 'T'));
  if (isNaN(d)) return str;
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const STATUS_LABEL = { running: '⏳ 진행 중', completed: '✅ 완료', failed: '❌ 실패' };
// 대상 경로 직접입력 시 클라이언트에서 먼저 걸러주는 형식 검사(서버에서도 동일하게 한 번 더
// 검사한다 — utils/storageBackup.js의 isValidTargetPath). 드라이브 문자(예: "E:") 단독이거나,
// 그 뒤에 하위경로가 붙은 형태(예: "E:\백업\내폴더")까지 허용한다.
const VALID_TARGET_PATH = /^[A-Za-z]:[\\/]?([^<>:"|?*\r\n]+)?$/;

export default function StorageBackupModal({ onClose }) {
  const [tab, setTab] = useState('run'); // 'run' | 'history' | 'settings'
  const [drives, setDrives] = useState([]);
  const [loadingDrives, setLoadingDrives] = useState(true);
  const [selectMode, setSelectMode] = useState('auto'); // 'auto'(자동감지 목록) | 'manual'(직접선택)
  const [selectedDrive, setSelectedDrive] = useState('');
  const [manualPath, setManualPath] = useState('');
  // "직접 선택" 버튼을 누르면 이 팝업(대상 드라이브를 고르는 별도의 작은 창)이 뜬다. 팝업 안에서
  // 확인을 눌러야 실제로 selectMode가 'manual'로 바뀌고 manualPath가 저장된다(취소하면 그대로).
  const [showManualPicker, setShowManualPicker] = useState(false);
  const [pickerDraft, setPickerDraft] = useState('');
  const [pickerMsg, setPickerMsg] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [running, setRunning] = useState(false);
  const [runningLogId, setRunningLogId] = useState(null);
  const [statusRow, setStatusRow] = useState(null);
  const [history, setHistory] = useState([]);
  const [itDept, setItDept] = useState('');
  const [msg, setMsg] = useState('');

  const loadDrives = () => {
    setLoadingDrives(true);
    API.get('/storage-backup/drives')
      .then(r => setDrives(r.data || []))
      .catch(() => setMsg('드라이브 목록을 가져오지 못했습니다.'))
      .finally(() => setLoadingDrives(false));
  };
  const loadHistory = () => {
    API.get('/storage-backup/history').then(r => setHistory(r.data || [])).catch(() => {});
  };
  const loadSettings = () => {
    API.get('/storage-backup/settings').then(r => setItDept(r.data?.itDept || '')).catch(() => {});
  };

  useEffect(() => { loadDrives(); loadHistory(); loadSettings(); }, []);

  // 실행 중인 백업이 있으면 2초마다 상태를 폴링해서 완료/실패를 감지한다.
  useEffect(() => {
    if (!runningLogId) return;
    const timer = setInterval(() => {
      API.get(`/storage-backup/status/${runningLogId}`).then(r => {
        setStatusRow(r.data);
        if (r.data.status !== 'running') {
          setRunning(false);
          setRunningLogId(null);
          loadHistory();
        }
      }).catch(() => {});
    }, 2000);
    return () => clearInterval(timer);
  }, [runningLogId]);

  // 자동감지 목록에서 고른 드라이브 문자, 또는 직접입력한 경로 — 실제 백업 대상으로 쓸 값.
  const targetValue = selectMode === 'manual' ? manualPath.trim() : selectedDrive;

  const handleRun = async () => {
    if (!targetValue) { setMsg(selectMode === 'manual' ? '대상 경로를 입력해주세요.' : '대상 드라이브를 선택해주세요.'); return; }
    if (selectMode === 'manual' && !VALID_TARGET_PATH.test(targetValue)) {
      setMsg('경로 형식이 올바르지 않습니다. (예: E:\\ 또는 E:\\백업\\내폴더)');
      return;
    }
    if (!confirmChecked) { setMsg('미러 복사 안내를 확인하고 체크해주세요.'); return; }
    setMsg('');
    setRunning(true);
    try {
      const drive = drives.find(d => d.letter === selectedDrive);
      const targetLabel = selectMode === 'manual' ? '직접선택' : (drive?.label || '');
      const res = await API.post('/storage-backup/run', { targetDrive: targetValue, targetLabel });
      setRunningLogId(res.data.logId);
      setStatusRow({ status: 'running' });
    } catch (err) {
      setRunning(false);
      setMsg(err.response?.data?.message || '백업 시작에 실패했습니다.');
    }
  };

  const openManualPicker = () => {
    setPickerDraft(manualPath);
    setPickerMsg('');
    setShowManualPicker(true);
  };
  const confirmManualPick = () => {
    const v = pickerDraft.trim();
    if (!v) { setPickerMsg('대상 경로를 입력해주세요.'); return; }
    if (!VALID_TARGET_PATH.test(v)) { setPickerMsg('경로 형식이 올바르지 않습니다. (예: E:\\ 또는 E:\\백업\\내폴더)'); return; }
    setManualPath(v);
    setSelectMode('manual');
    setConfirmChecked(false);
    setShowManualPicker(false);
  };

  const handleDeleteLog = async (id) => {
    if (!window.confirm('이 이력을 삭제하시겠습니까? (대상 드라이브의 실제 백업 파일은 그대로 유지됩니다)')) return;
    try {
      await API.delete(`/storage-backup/${id}`);
      loadHistory();
    } catch (err) {
      setMsg(err.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await API.put('/storage-backup/settings', { itDept: itDept.trim() });
      setMsg('저장되었습니다.');
    } catch (err) {
      setMsg(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
    }
  };

  const tabBtn = (key, label) => (
    <button onClick={() => { setTab(key); setMsg(''); }}
      style={{ padding:'8px 16px', border:'none', borderBottom: tab===key ? '2px solid #185FA5' : '2px solid transparent',
        background:'none', color: tab===key ? '#185FA5' : '#888', fontWeight: tab===key ? 700 : 500,
        fontSize:14, cursor:'pointer' }}>
      {label}
    </button>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:3000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:12, width:560, maxHeight:'85vh', overflowY:'auto',
        boxShadow:'0 8px 30px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding:'20px 24px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontSize:17, fontWeight:700, margin:0 }}>🗄️ 스토리지 백업</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ display:'flex', margin:'14px 24px 0', borderBottom:'1.5px solid #eee' }}>
          {tabBtn('run', '백업 실행')}
          {tabBtn('history', '이력')}
          {tabBtn('settings', '알림 설정')}
        </div>

        {msg && <div style={{ margin:'12px 24px 0', fontSize:13, color:'#dc2626' }}>{msg}</div>}

        {tab === 'run' && (
          <div style={{ padding:'16px 24px 24px' }}>
            <div style={{ fontSize:12, color:'#666', background:'#f8fafc', border:'1px solid #eee',
              borderRadius:8, padding:12, marginBottom:14, lineHeight:1.6 }}>
              서버 컴퓨터에 장착된 하드디스크/USB 메모리를 선택하면, 스토리지(D:\storage) 전체가
              선택한 드라이브의 <b>IMS_Storage_Backup</b> 폴더로 <b>완전히 동일하게(미러)</b> 복사됩니다.
              대상 드라이브의 그 폴더 안에 이미 있던, 원본에는 없는 파일/폴더는 <b style={{ color:'#dc2626' }}>함께 삭제됩니다</b>.
              한 달에 한 번씩 백업하는 것을 권장합니다.
            </div>

            <div style={{ marginBottom:10 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                <label style={{ fontSize:13, fontWeight:600, color:'#333' }}>대상 드라이브</label>
                <div style={{ display:'flex', gap:4 }}>
                  <button onClick={() => { setSelectMode('auto'); setConfirmChecked(false); setMsg(''); }}
                    style={{ padding:'4px 10px', fontSize:12, borderRadius:6, cursor:'pointer',
                      border: selectMode==='auto' ? '1.5px solid #185FA5' : '1px solid #ddd',
                      background: selectMode==='auto' ? '#eaf2fb' : '#fff',
                      color: selectMode==='auto' ? '#185FA5' : '#888', fontWeight: selectMode==='auto' ? 700 : 500 }}>
                    감지된 목록
                  </button>
                  <button onClick={openManualPicker}
                    style={{ padding:'4px 10px', fontSize:12, borderRadius:6, cursor:'pointer',
                      border: selectMode==='manual' ? '1.5px solid #185FA5' : '1px solid #ddd',
                      background: selectMode==='manual' ? '#eaf2fb' : '#fff',
                      color: selectMode==='manual' ? '#185FA5' : '#888', fontWeight: selectMode==='manual' ? 700 : 500 }}>
                    직접 선택
                  </button>
                </div>
              </div>

              {selectMode === 'auto' ? (
                loadingDrives ? (
                  <div style={{ fontSize:13, color:'#999' }}>드라이브 목록을 불러오는 중...</div>
                ) : drives.length === 0 ? (
                  <div style={{ fontSize:13, color:'#999' }}>
                    장착된 백업 대상 드라이브(HDD/USB)를 찾을 수 없습니다. 드라이브를 연결한 뒤
                    <button onClick={loadDrives} style={{ marginLeft:6, background:'none', border:'none',
                      color:'#185FA5', cursor:'pointer', fontSize:13, textDecoration:'underline' }}>다시 확인</button>
                    , 또는 위에서 "직접 선택"을 눌러 경로를 직접 지정할 수 있습니다.
                  </div>
                ) : (
                  <select value={selectedDrive} onChange={e => { setSelectedDrive(e.target.value); setConfirmChecked(false); }}
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
                      fontSize:14, boxSizing:'border-box', background:'#fff' }}>
                    <option value="">— 선택하세요 —</option>
                    {drives.map(d => (
                      <option key={d.letter} value={d.letter}>
                        {d.letter} {d.label ? `(${d.label})` : ''} — 여유공간 {fmtBytes(d.freeBytes)} / 전체 {fmtBytes(d.totalBytes)}
                      </option>
                    ))}
                  </select>
                )
              ) : (
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
                    fontSize:14, background:'#f8fafc', color: manualPath ? '#333' : '#999' }}>
                    {manualPath || '선택된 경로가 없습니다'}
                  </div>
                  <button onClick={openManualPicker}
                    style={{ padding:'9px 14px', background:'#fff', color:'#185FA5', border:'1px solid #185FA5',
                      borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                    변경
                  </button>
                </div>
              )}
            </div>

            {targetValue && (
              <label style={{ display:'flex', alignItems:'flex-start', gap:8, fontSize:13, color:'#555',
                marginBottom:16, cursor:'pointer' }}>
                <input type="checkbox" checked={confirmChecked} onChange={e => setConfirmChecked(e.target.checked)}
                  style={{ marginTop:2 }} />
                <span>{targetValue}의 IMS_Storage_Backup 폴더를 스토리지와 완전히 동일하게
                  맞춘다는 것(대상 전용 파일 삭제 포함)을 확인했습니다.</span>
              </label>
            )}

            {(running || statusRow?.status === 'running') && (
              <div style={{ fontSize:13, color:'#185FA5', background:'#eaf2fb', borderRadius:8,
                padding:'10px 14px', marginBottom:14 }}>
                ⏳ 백업이 진행 중입니다... (완료될 때까지 이 창을 닫아도 서버에서는 계속 진행됩니다)
              </div>
            )}
            {statusRow?.status === 'completed' && (
              <div style={{ fontSize:13, color:'#0F6E56', background:'#e6f7f1', borderRadius:8,
                padding:'10px 14px', marginBottom:14 }}>
                ✅ 백업이 완료되었습니다. (파일 {statusRow.file_count ?? 0}개, {fmtBytes(statusRow.byte_count)})
              </div>
            )}
            {statusRow?.status === 'failed' && (
              <div style={{ fontSize:13, color:'#dc2626', background:'#fee2e2', borderRadius:8,
                padding:'10px 14px', marginBottom:14, whiteSpace:'pre-wrap' }}>
                ❌ 백업이 실패했습니다.{statusRow.error_message ? `\n${statusRow.error_message}` : ''}
              </div>
            )}
          </div>
        )}

        {tab === 'history' && (
          <div style={{ padding:'16px 24px 24px' }}>
            {history.length === 0 ? (
              <div style={{ padding:24, textAlign:'center', color:'#aaa', fontSize:13 }}>백업 이력이 없습니다.</div>
            ) : (
              <div style={{ border:'1px solid #eee', borderRadius:8, maxHeight:360, overflowY:'auto' }}>
                {history.map((h, idx) => (
                  <div key={h.id} style={{ padding:'10px 14px', borderTop: idx===0 ? 'none' : '1px solid #f0f0f0' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ fontSize:13, fontWeight:600 }}>{STATUS_LABEL[h.status] || h.status} · {h.target_drive} {h.target_label ? `(${h.target_label})` : ''}</span>
                      <button onClick={() => handleDeleteLog(h.id)}
                        style={{ background:'none', border:'none', color:'#dc2626', fontSize:12, cursor:'pointer' }}>삭제</button>
                    </div>
                    <div style={{ fontSize:12, color:'#888', marginTop:4 }}>
                      {fmtDT(h.started_at)} 시작{h.completed_at ? ` · ${fmtDT(h.completed_at)} 종료` : ''}
                      {h.status === 'completed' ? ` · 파일 ${h.file_count ?? 0}개, ${fmtBytes(h.byte_count)}` : ''}
                      {h.started_by_name ? ` · ${h.started_by_name}` : ''}
                    </div>
                    {h.status === 'failed' && h.error_message && (
                      <div style={{ fontSize:11, color:'#dc2626', marginTop:4, whiteSpace:'pre-wrap' }}>{h.error_message}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'settings' && (
          <div style={{ padding:'16px 24px 24px' }}>
            <div style={{ fontSize:12, color:'#999', marginBottom:10, lineHeight:1.6 }}>
              백업 후 1개월이 지나면 최고관리자·전산담당자·대표이사에게 안내가 표시됩니다.
              "전산담당자"는 아래 지정한 부서의 부서장으로 판단합니다(조직도 &gt; 조직구성에서
              부서장 직급이 지정되어 있어야 합니다). 대표이사는 조직도의 대표이사 탭에서 직원
              계정과 연결해야 알림 대상이 됩니다.
            </div>
            <label style={{ fontSize:13, fontWeight:600, color:'#333', display:'block', marginBottom:6 }}>전산담당 부서명</label>
            <div style={{ display:'flex', gap:8 }}>
              <input value={itDept} onChange={e => setItDept(e.target.value)}
                placeholder="예: 전산팀"
                style={{ flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' }} />
              <button onClick={handleSaveSettings}
                style={{ padding:'9px 16px', background:'#185FA5', color:'#fff', border:'none',
                  borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                저장
              </button>
            </div>
          </div>
        )}

        <div style={{ padding:'0 24px 20px', display:'flex', gap:8 }}>
          {tab === 'run' && (
            <button onClick={handleRun} disabled={running || !targetValue || !confirmChecked}
              style={{ flex:1, padding:'11px', background: (running || !targetValue || !confirmChecked) ? '#aaa' : '#185FA5',
                color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700,
                cursor: (running || !targetValue || !confirmChecked) ? 'not-allowed' : 'pointer' }}>
              {running ? '백업 진행 중...' : '🗄️ 백업 시작'}
            </button>
          )}
          <button onClick={onClose}
            style={{ flex:1, padding:'11px', background:'#f0f0f0', color:'#555', border:'none',
              borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            닫기
          </button>
        </div>
      </div>

      {/* "직접 선택" 버튼을 누르면 뜨는 대상 드라이브 선택 창(팝업). 여기서 확인을 눌러야
          실제로 대상이 바뀐다(취소하면 이전 선택이 그대로 유지된다). */}
      {showManualPicker && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.3)', zIndex:3100,
          display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setShowManualPicker(false)}>
          <div style={{ background:'#fff', borderRadius:12, width:420, padding:'20px 22px',
            boxShadow:'0 8px 30px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <h4 style={{ fontSize:15, fontWeight:700, margin:'0 0 12px' }}>대상 드라이브 선택</h4>
            <input value={pickerDraft}
              onChange={e => setPickerDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') confirmManualPick(); }}
              placeholder="예: E:\ 또는 E:\백업\내폴더"
              autoFocus
              style={{ width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
                fontSize:14, boxSizing:'border-box' }} />
            <div style={{ fontSize:11.5, color:'#999', marginTop:8, lineHeight:1.5 }}>
              서버 컴퓨터에서 인식하는 드라이브 문자와 경로를 입력합니다. 자동감지 목록에 나타나지
              않는 드라이브(네트워크 드라이브 등)나 특정 하위폴더를 지정할 때 사용하세요.
            </div>
            {pickerMsg && <div style={{ fontSize:12.5, color:'#dc2626', marginTop:8 }}>{pickerMsg}</div>}
            <div style={{ display:'flex', gap:8, marginTop:16 }}>
              <button onClick={() => setShowManualPicker(false)}
                style={{ flex:1, padding:'10px', background:'#f0f0f0', color:'#555', border:'none',
                  borderRadius:8, cursor:'pointer', fontSize:13.5, fontWeight:600 }}>
                취소
              </button>
              <button onClick={confirmManualPick}
                style={{ flex:1, padding:'10px', background:'#185FA5', color:'#fff', border:'none',
                  borderRadius:8, cursor:'pointer', fontSize:13.5, fontWeight:700 }}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
